import React, { useState } from "react";
import {
  Form,
  Input,
  DatePicker,
  Select,
  Button,
  Row,
  Col,
  InputNumber,
} from "antd";
import {
  CheckCircleTwoTone,
  FileTextTwoTone,
  PlusOutlined,
} from "@ant-design/icons";
import styles from "./index.module.scss";
import { ExerciseItem } from "../Create";
import { useMiddahTableGet } from "../../../../../../../hooks/api/middot";
import { LazySelect } from "../../../../../../Common/SelectLazyLoader";
import { useExerciseTableGet } from "../../../../../../../hooks/api/exercises";

interface Exercise {
  id: string;
  name: string;
}

interface ChartCreateFormProps {
  vaadId: string;
  vaadType?: string;
  exercises: ExerciseItem[];
  setExercises: React.Dispatch<React.SetStateAction<ExerciseItem[]>>;
  defaultTasks?: { id: string; name: string; icon: React.ReactNode }[];
  availableExercises?: Exercise[];
  initialMiddah?: { id: string; name: string };
  initialExercises?: { id: string; name: string }[];
}

const DEFAULT_TASKS_WOMEN = [
  { id: "checkin", name: "Check-in", icon: <CheckCircleTwoTone /> },
  { id: "reading1", name: "Reading", icon: <FileTextTwoTone /> },
];

const DEFAULT_TASKS_MEN = [
  { id: "checkin", name: "Check-in", icon: <CheckCircleTwoTone /> },
  { id: "reading1", name: "Reading", icon: <FileTextTwoTone /> },
  { id: "reading2", name: "Reading", icon: <FileTextTwoTone /> },
];

export default function ChartCreateForm({
  exercises,
  setExercises,
  initialMiddah,
  initialExercises,
  vaadType,
}: ChartCreateFormProps): React.ReactElement {
  const form = Form.useFormInstance();
  const middahTableGet = useMiddahTableGet();
  const exerciseTableGet = useExerciseTableGet();
  const defaultTasks =
    vaadType === "women" ? DEFAULT_TASKS_WOMEN : DEFAULT_TASKS_MEN;

  // Key для принудительного обновления LazySelect упражнений
  const [exerciseSelectKey, setExerciseSelectKey] = useState(0);

  const fetchMiddahOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const response = await middahTableGet.fetch({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
    });
    return response?.data || [];
  };

  const middahInitialOptions = initialMiddah
    ? [{ label: initialMiddah.name, value: initialMiddah.id }]
    : undefined;

  const exerciseInitialOptions = initialExercises?.map((e) => ({
    label: e.name,
    value: e.id,
  }));

  const mapMiddahOption = (item: any) => ({
    label: item.name,
    value: item.id,
  });

  const fetchExerciseOptions = async (params: {
    search?: string;
    page: number;
    pageSize: number;
  }) => {
    const middahId = form.getFieldValue("middahId");

    if (!middahId) {
      return [];
    }

    const response = await exerciseTableGet.fetch({
      page: params.page,
      pageSize: params.pageSize,
      search: params.search,
      middahId: middahId,
    });

    return response?.data || [];
  };

  const mapExerciseOption = (item: any) => ({
    label: item.name,
    value: item.id,
  });

  const handleMiddahChange = () => {
    setExercises([]);
    setExerciseSelectKey((prev) => prev + 1);
  };

  const addExercise = () => {
    if (exercises.length < 3) {
      setExercises([...exercises, { id: Date.now(), repetitions: 1 }]);
    }
  };

  const removeExercise = (id: number) => {
    setExercises(exercises.filter((e) => e.id !== id));
    // Form fields are keyed by the stable exercise id (see Form.Item names), so
    // removing one never shifts the others. Just drop this id's subtree.
    form.setFieldValue(["exercises", String(id)], undefined);
  };

  const updateExercise = (id: number, field: string, value: any) => {
    setExercises(
      exercises.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    );
  };

  const remainingSlots = 3 - exercises.length;

  return (
    <Form
      form={form}
      layout="vertical"
      name="chartForm"
      className={styles.chartForm}
    >
      {/* Chart Name */}
      <Form.Item
        label="Chart Name"
        name="name"
        rules={[{ required: true, message: "Chart name is required" }]}
      >
        <Input placeholder="e.g., Simcha Chart 3" />
      </Form.Item>

      {/* Middah Name */}
      <Form.Item
        label="Middah Name"
        name="middahId"
        rules={[{ required: true, message: "Middah is required" }]}
      >
        <LazySelect
          fetchOptions={fetchMiddahOptions}
          mapOption={mapMiddahOption}
          pageSize={20}
          initialOptions={middahInitialOptions}
          onChange={handleMiddahChange}
        />
      </Form.Item>

      {/* Dates */}
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Start Date"
            name="startDate"
            rules={[{ required: true, message: "Start date is required" }]}
          >
            <DatePicker
              placeholder="Select date"
              format="MMM DD, YYYY"
              style={{ width: "100%" }}
              disabledDate={(current) =>
                current && current.isBefore(new Date(), "day")
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            label="End Date"
            name="endDate"
            rules={[
              { required: true, message: "End date is required" },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const startDate = getFieldValue("startDate");
                  if (!value || !startDate) {
                    return Promise.resolve();
                  }
                  if (value.isBefore(startDate)) {
                    return Promise.reject(
                      new Error("End date must be after start date"),
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <DatePicker
              placeholder="Select date"
              format="MMM DD, YYYY"
              style={{ width: "100%" }}
              disabledDate={(current) => {
                const startDate = form.getFieldValue("startDate");
                return (
                  (current && current.isBefore(new Date(), "day")) ||
                  (startDate && current && current.isBefore(startDate, "day"))
                );
              }}
            />
          </Form.Item>
        </Col>
      </Row>

      <div className={styles.dateHint}>
        Chart dates must not overlap with existing charts in this Vaad
      </div>

      {/* Default Tasks */}
      <div className={styles.defaultTasksTitle}>Default Tasks</div>
      <div className={styles.defaultTasksContainer}>
        {defaultTasks.map((task) => (
          <div key={task.id} className={styles.defaultTaskCard}>
            <div className={styles.header}>
              <div className={styles.defaultTaskIcon}>{task.icon}</div>
              <div className={styles.defaultTaskName}>{task.name}</div>
            </div>
            <div className={styles.defaultTaskBadge}>Always included</div>
          </div>
        ))}
      </div>

      {/* Custom Exercises */}
      <div className={styles.customExercisesTitle}>
        <span>Custom Exercises</span>
        <span className={styles.remainingHint}>(select up to 3)</span>
      </div>

      {exercises.map((exercise, index) => (
        <div key={exercise.id} className={styles.exerciseCard}>
          <div className={styles.exerciseHeader}>
            <span className={styles.exerciseName}>Exercise {index + 1}</span>
            {(index > 0 || exercises.length > 1) && (
              <Button
                type="link"
                danger
                onClick={() => removeExercise(exercise.id)}
              >
                Remove
              </Button>
            )}
          </div>

          <Form.Item
            label="Select Exercise"
            name={["exercises", String(exercise.id), "exerciseId"]}
            rules={[{ required: true, message: "Exercise is required" }]}
          >
            <LazySelect
              key={`${exerciseSelectKey}-${exercise.id}`}
              fetchOptions={fetchExerciseOptions}
              mapOption={mapExerciseOption}
              initialOptions={exerciseInitialOptions}
              pageSize={20}
            />
          </Form.Item>

          <Form.Item
            label="Repetitions per Day"
            name={["exercises", String(exercise.id), "repetitions"]}
          >
            <InputNumber
              min={1}
              max={3}
              defaultValue={1}
              value={exercise.repetitions}
              onChange={(val) =>
                updateExercise(exercise.id, "repetitions", val)
              }
              style={{ width: "100%" }}
            />
          </Form.Item>
        </div>
      ))}

      {remainingSlots > 0 && (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          onClick={addExercise}
          style={{
            borderColor: "#1677FF",
            color: "#1677FF",
          }}
          className={styles.addExerciseButton}
        >
          Add exercise ({remainingSlots} remaining)
        </Button>
      )}
    </Form>
  );
}
